import React, { ReactNode } from "react";
import { DefinedVariable, pluginInfo } from "./allData";
import { toast } from "@components/ui/use-toast";
import {
  Braces,
  Copy,
  Trash2,
  RefreshCcw,
  ChevronDown,
  ChevronUp,
  Plug2,
} from "lucide-react";
import Confirmation from "@components/common/ConfirmDialog";
import InfoTooltip from "@components/common/InfoTooltip";
import {
  commonDeleteApi,
  fetchConfigPage,
  fetchLogicPageInfo,
  sendRequest,
} from "./createPluginApi";
import { useMutation, useQuery } from "react-query";
import PageLoader from "@components/loaders/PageLoader";
import {
  useMeshPluginContext,
  MeshPluginActions,
} from "@services/providers/MeshPlugin";

type FormWrapperProps = {
  title: string;
  children: ReactNode;
  option?: string;
  goToPage?: (index: number) => void;
  selectedApiData?: pluginInfo | pluginInfo[];
  definedVariables?: DefinedVariable[];
  setDefinedVariables?: (value: DefinedVariable[]) => void;
  selectedLogicBlockId?: number | null;
  logicType?: any[];
  uiPages?: any[];
  configurationData?: any[];
  pluginId?: string | null;
};

export function FormWrapper({
  title,
  option,
  children,
  goToPage,
  selectedApiData,
  definedVariables,
  setDefinedVariables,
  selectedLogicBlockId,
  logicType,
  uiPages,
  configurationData,
  pluginId,
}: FormWrapperProps) {
  const {
    selectedConfigData,
    selectedLogicName,
    hasUnsavedFields,
    hasUnsavedHelpDocs,
    hasUnsavedGeneralInfo,
    selectedSettingsPageIndex,
    dispatch,
    hasLogicPage,
    hasTempConfigPage,
  } = useMeshPluginContext();
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);
  const [confirmationOpen, setConfirmationOpen] =
    React.useState<boolean>(false);
  const [variableToDelete, setVariableToDelete] = React.useState<
    number | string | null | undefined
  >(null);
  const [bundleTitle, setBundleTitle] = React.useState<string>("");
  const [selectedConfigIndex, setSelectedConfigIndex] = React.useState<
    number | null
  >(null);
  const [unsavedConfirmationOpen, setUnsavedConfirmationOpen] =
    React.useState(false);
  const [pendingPageKey, setPendingPageKey] = React.useState<number | null>(
    null,
  );
  // const [data, setData] = React.useState<any>({});
  const [config, setConfig] = React.useState<any>({});
  const [loading, setLoading] = React.useState<boolean>(false);
  const [isConfigDataCollapsed, setIsConfigDataCollapsed] =
    React.useState(false);
  const [isCommonVariablesCollapsed, setIsCommonVariablesCollapsed] =
    React.useState(false);
  const [isLocalVariablesCollapsed, setIsLocalVariablesCollapsed] =
    React.useState(false);

  const tempVar = React.useRef<DefinedVariable | null>(null);

  const EXCLUDE_DATA_KEYS = ["uid", "isDecript"];

  // Check if we're in edit mode
  const isEditMode = React.useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("edit") === "1";
  }, []);

  // Fetch logic page info for authentication variable refresh
  const { data: logicPageInfo } = useQuery({
    queryKey: ["fetchLogicPageData", pluginId],
    queryFn: () => fetchLogicPageInfo(pluginId ?? ""),
    enabled: !!pluginId && isEditMode && hasLogicPage,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  // Fetch config page data
  const { data: configPageData } = useQuery(
    ["configPage", pluginId, "temp"],
    () => fetchConfigPage(pluginId ?? "", "temp"),
    {
      enabled: !!pluginId && isEditMode && hasTempConfigPage,
    },
  );

  // mutation for delete variable
  const deleteVariable = useMutation({
    mutationFn: (payload: any) =>
      commonDeleteApi(
        payload.plugin_id,
        payload.delete_type,
        payload.bundle_uid,
        payload.request_uid,
        payload.variable_uid,
      ),

    onMutate: () => {
      setLoading(true);
    },

    onSettled: () => {
      setLoading(false);
    },

    onSuccess: () => {
      if (!definedVariables || !setDefinedVariables || !variableToDelete)
        return;
      const variable = definedVariables.find(
        (variable) => variable.id === variableToDelete,
      );

      toast({
        title: "Deleted!",
        description: `Variable {{${variable?.name}}} has been deleted.`,
        variant: "success",
        duration: 2000,
      });

      const updatedVariables = definedVariables.filter(
        (variable) => variable.id !== variableToDelete,
      );
      setDefinedVariables(updatedVariables);

      setConfirmationOpen(false);
      setVariableToDelete(null);
    },
    onError: () => {
      toast({
        title: "Error",
        description: `Failed to delete variable.`,
        variant: "destructive",
        duration: 2000,
      });
      setConfirmationOpen(false);
      setVariableToDelete(null);
    },
  });

  // Set initial config and data when configPageData changes
  React.useEffect(() => {
    if (configPageData?.data) {
      const [tabId, items] =
        (Object.entries(configPageData.data)[0] as [string, any[]]) || [];
      const item = items?.[0];
      if (!item) return;

      setConfig({
        tabId,
        uid: item.uid,
        configType: "temp",
      });

      // setData(
      //   Object.fromEntries(
      //     Object.entries(item).filter(
      //       ([key]) => !EXCLUDE_DATA_KEYS.includes(key),
      //     ),
      //   ),
      // );
    }
  }, [configPageData]);

  // Mutation to refresh authentication variable
  const setAuthVariable = useMutation({
    mutationFn: (payload: any) => sendRequest(payload),

    onMutate: () => {
      setLoading(true);
    },

    onSettled: () => {
      setLoading(false);
      tempVar.current = null;
    },

    // Replace the onSuccess handler around line 200

    onSuccess: (response: any) => {
      const resValue = response?.body?.data ?? response?.body ?? response;

      if (!tempVar.current) {
        toast({
          title: "Error",
          description: `Temporary variable data is missing.`,
          variant: "destructive",
          duration: 2000,
        });
        return;
      }

      if (!definedVariables) {
        toast({
          title: "Error",
          description: `Defined variables are not available to update.`,
          variant: "destructive",
          duration: 2000,
        });
        return;
      }

      const matchingVariable: any = definedVariables?.find(
        (variable) => variable?.name == tempVar?.current?.name,
      );

      if (!matchingVariable) {
        toast({
          title: "Error",
          description: `Could not find the variable {{${tempVar.current?.name}}} to update.`,
          variant: "destructive",
          duration: 2000,
        });
        return;
      }

      const path = tempVar?.current?.sourcePath || "";

      //  Handle array indexing in the path
      let currentValue = resValue;

      if (path === "" || path === "root") {
        currentValue = resValue;
      } else {
        // Convert array notation [0] to .0 for easier handling
        const normalizedPath = path.replace(/\[(\d+)\]/g, ".$1");
        const pathParts = normalizedPath.split(".").filter(Boolean);

        for (const part of pathParts) {
          if (currentValue === undefined || currentValue === null) {
            console.error(`Current value is null/undefined at part "${part}"`);
            break;
          }

          // Check if part is numeric (array index)
          const index = Number(part);
          if (!isNaN(index)) {
            if (Array.isArray(currentValue)) {
              if (index >= 0 && index < currentValue.length) {
                currentValue = currentValue[index];
              } else {
                console.error(
                  `Array index ${index} out of bounds. Array length: ${currentValue.length}`,
                );
                currentValue = undefined;
                break;
              }
            } else {
              console.error(
                `Cannot access index ${index} on non-array:`,
                typeof currentValue,
              );
              currentValue = undefined;
              break;
            }
          }
          // Handle regular property access
          else if (typeof currentValue === "object" && part in currentValue) {
            currentValue = currentValue[part];
          }
          // Path not found
          else {
            console.error(
              `Path part "${part}" not found in current value. Available keys:`,
              typeof currentValue === "object"
                ? Object.keys(currentValue)
                : "N/A",
            );
            currentValue = undefined;
            break;
          }
        }
      }

      // Save value in the exact variable and rest in the same
      const updatedVariables = definedVariables?.map((variable) => {
        if (variable?.name === matchingVariable?.name) {
          return {
            ...variable,
            value: currentValue,
          };
        }
        return variable;
      });

      setDefinedVariables?.(updatedVariables);

      if (
        currentValue === undefined ||
        currentValue === null ||
        currentValue === "" ||
        (Array.isArray(currentValue) && currentValue.length === 0)
      ) {
        toast({
          title: "Refreshed but no value found!",
          description: `Variable {{${tempVar.current?.name}}} refreshed but no value found to replace.`,
          variant: "destructive",
          duration: 4000,
        });
      } else {
        toast({
          title: "Refreshed and replaced!",
          description: `Variable {{${tempVar.current?.name}}} has been refreshed and updated.`,
          variant: "success",
          duration: 3000,
        });
      }

      tempVar.current = null;
    },

    // Enhanced error handling
    onError: (error: any) => {
      let errorDescription = "Failed to refresh variable.";

      // Try to extract the detailed error message from the response
      try {
        // Check response.data.message first (most common case)
        if (error?.response?.data?.message) {
          const message = error.response.data.message;

          // Try to parse if it contains JSON error details
          const jsonMatch = message.match(/\{.*\}/s);
          if (jsonMatch) {
            try {
              const errorObj = JSON.parse(jsonMatch[0]);
              errorDescription =
                errorObj.error_description || errorObj.message || message;
            } catch {
              errorDescription = message;
            }
          } else {
            errorDescription = message;
          }
        }
        // Fallback to error.message
        else if (error?.message) {
          const jsonMatch = error.message.match(/\{.*\}/s);
          if (jsonMatch) {
            try {
              const errorObj = JSON.parse(jsonMatch[0]);
              errorDescription =
                errorObj.error_description || errorObj.message || error.message;
            } catch {
              errorDescription = error.message;
            }
          } else {
            errorDescription = error.message;
          }
        }
        // Direct string error
        else if (typeof error === "string") {
          errorDescription = error;
        }
      } catch (parseError) {
        // If parsing fails, use the original error
        errorDescription =
          error?.response?.data?.message || error?.message || String(error);
      }

      toast({
        title: "Error",
        description: errorDescription,
        variant: "destructive",
        duration: 5000,
      });

      // set the value to null in case of error
      if (tempVar.current && definedVariables && setDefinedVariables) {
        const updatedVariables: any[] = definedVariables?.map((variable) => {
          if (variable?.name === tempVar.current?.name) {
            return {
              ...variable,
              value: null,
            };
          }
          return variable;
        });
        setDefinedVariables(updatedVariables);
      }

      tempVar.current = null;
    },
  });

  // Get the selected settings page based on the index
  const selectedPage =
    selectedSettingsPageIndex !== null &&
    selectedSettingsPageIndex !== undefined &&
    uiPages
      ? uiPages[selectedSettingsPageIndex]
      : null;

  // Normalize the option to lowercase for consistent comparison
  const normalizedOption = option?.toLowerCase();

  // Define a mapping for options to display names
  const optionMap: Record<string, string> = {
    info: "General Info",
    ui: "UI Pages",
    logic: "Logic",
  };

  // Select the appropriate option based on the normalized option
  const selectedOption = optionMap[normalizedOption || ""];

  // If the option is not recognized, default to 'General Info'
  const options = ["General Info", "UI Pages", "Logic"];

  const handleChangePage = (key: string) => {
    const index =
      key === "General Info"
        ? 1
        : key === "UI Pages"
          ? 2
          : key === "Logic"
            ? 3
            : undefined;

    if (index === undefined) return;

    if (hasUnsavedHelpDocs || hasUnsavedFields || hasUnsavedGeneralInfo) {
      setPendingPageKey(index);
      setUnsavedConfirmationOpen(true);
      return;
    }

    goToPage?.(index);
  };

  // Function to handle variable click and copy to clipboard
  const handleVariableClick = (variable: DefinedVariable) => {
    const newValue = `{{${variable?.name}}}`;

    toast({
      title: `Copied!`,
      description: `Variable ${newValue} copied.`,
      variant: "info",
      duration: 2000,
    });

    // Optional: Copy to clipboard
    navigator.clipboard.writeText(newValue).catch(() => {});
  };

  // onclose the confirmation
  const onClose = () => {
    setConfirmationOpen(false);
    setVariableToDelete(null);
  };

  // set selected config data if only one configuration is present
  React.useEffect(() => {
    if (
      selectedApiData &&
      Array.isArray(selectedApiData) &&
      selectedApiData.length == 1
    ) {
      dispatch({
        type: MeshPluginActions.SET_SELECTED_CONFIG_DATA,
        payload: selectedApiData[0],
      });
    }
  }, [selectedApiData]);

  // Show all common variables
  const commonVariables = definedVariables?.filter((v) => {
    const normalizedVariableType =
      selectedLogicName === "Data List" ? "Data list" : selectedLogicName;
    return (
      (v?.logType === selectedLogicName ||
        v.variableType === selectedLogicName ||
        v.variableType === normalizedVariableType) &&
      v.isCommon
    );
  });

  // Show variables based on selected logic name and block id
  const nonCommonVariables = definedVariables?.filter((v: any) => {
    const normalizedVariableType =
      selectedLogicName === "Data List" ? "Data list" : selectedLogicName;
    return (
      !v.isCommon &&
      (v?.logType === selectedLogicName ||
        v.variableType === selectedLogicName ||
        v.variableType === normalizedVariableType) &&
      v?.sourceBundleId?.toString() === selectedLogicBlockId?.toString()
    );
  });

  React.useEffect(() => {
    const temp = logicType?.find(
      (item: any) => item.name === selectedLogicName,
    );
    const bundle = temp?.data?.find(
      (block: any) => block.id === selectedLogicBlockId,
    );
    const currentBundleTitle = bundle?.title || "";
    setBundleTitle(currentBundleTitle);
  }, [selectedLogicName, selectedLogicBlockId]);

  // function to render variable preview
  const renderVariablePreview = (variable: DefinedVariable) => {
    const baseClass = "text-muted-foreground truncate w-[90px]";
    const baseStyle = { fontSize: "9px" };
    const refreshStyle = { fontSize: "11px" };

    const val = variable.value;
    // const refreshable = variable.isAuthentication;

    if (val === undefined || (Array.isArray(val) && val.length === 0)) {
      return (
        <div className={`text-primary`} style={refreshStyle}>
          empty value !
        </div>
      );
    }

    if (val === null || val === "") {
      return (
        <div className={`text-primary`} style={refreshStyle}>
          <span className="text-primaryGreen">refresh variable</span>
        </div>
      );
    }

    if (Array.isArray(val)) {
      if (val.length > 0 && typeof val[0] === "object") {
        const [key, value] = Object.entries(val[0])[0] || [];
        return (
          <div className={baseClass} style={baseStyle}>
            {key}: {String(value)}
          </div>
        );
      } else {
        return (
          <div className={baseClass} style={baseStyle}>
            {String(val[0])}
          </div>
        );
      }
    }

    if (typeof val === "object") {
      const [key, value] = Object.entries(val)[0] || [];
      return (
        <div className={baseClass} style={baseStyle}>
          {key}: {String(value)}
        </div>
      );
    }

    return (
      <div className={baseClass} style={baseStyle}>
        {String(val)}
      </div>
    );
  };

  // Add this interpolation function near the top of FormWrapper component (after the imports)
  function interpolateDefinedVariablesOnly(
    input: string,
    definedVars: DefinedVariable[] = [],
  ): string {
    if (!input || typeof input !== "string") return input;

    return input.replace(/{{\s*(.*?)\s*}}/g, (match, key) => {
      // Skip config variables
      if (key.startsWith("config.")) return match;

      // Handle array-style access: values[1].id
      const arrayMatch = key.match(/^(\w+)\[(\d+)\]\.(\w+)$/);
      if (arrayMatch) {
        const [, arrayName, indexStr, prop] = arrayMatch;
        const variable = definedVars.find((v) => v.name === arrayName);
        if (!variable || !Array.isArray(variable.value)) return match;

        const index = parseInt(indexStr, 10);
        const item = variable.value[index];
        if (!item || !(prop in item)) return match;

        return String(item[prop]);
      }

      // Simple variable (no array index)
      const found = definedVars.find((v) => v.name === key);
      return found?.value != null ? String(found.value) : match;
    });
  }

  // function to handle authentication variable refresh
  // Extract variable names from a string (including {{variable}} patterns)
  function extractVariableNames(input: string): string[] {
    if (!input || typeof input !== "string") return [];

    const matches = input.matchAll(/{{\s*(\w+)(?:\[\d+\]\.?\w*)?\s*}}/g);
    const names = new Set<string>();

    for (const match of matches) {
      const fullMatch = match[1];
      // Extract base variable name (before any array indexing)
      const baseName = fullMatch.split("[")[0];
      if (baseName && !baseName.startsWith("config.")) {
        names.add(baseName);
      }
    }

    return Array.from(names);
  }

  const handleIsAuthRefresh = (variable: DefinedVariable) => {
    // Check if multiple configurations exist and none is selected
    if (forms.length > 1 && selectedConfigIndex === null) {
      toast({
        title: "Configuration Required",
        description:
          "Please select a configuration before refreshing variables.",
        variant: "destructive",
        duration: 3000,
      });
      setIsConfigDataCollapsed(false);
      return;
    }

    // Get dependencies for this variable
    const bundleId = variable?.sourceBundleId;
    const requestId = variable?.sourceRequestId;

    // Use current state from logicType instead of saved data from logicPageInfo
    const allBundles =
      logicType?.find((logic) => logic?.name === selectedLogicName)?.data || [];
    const bundle = allBundles?.find(
      (bundle: any) => bundle?.bundleId == bundleId || bundle?.id == bundleId,
    );

    if (bundle) {
      // Use finalRequestData which contains the updated values
      const requestDataItem = bundle?.finalRequestData?.request?.find(
        (req: any) => req?.requestId == requestId || req?.id == requestId,
      );

      if (requestDataItem) {
        // Collect all text fields that might contain variables
        const textsToCheck = [
          requestDataItem.url || "",
          requestDataItem.body || "",
          requestDataItem.auth?.token || "",
          ...(requestDataItem.headers || []).map((h: any) => h.key || ""),
          ...(requestDataItem.headers || []).map((h: any) => h.value || ""),
        ];

        // Extract variable names from all fields
        const varNames = new Set<string>();
        textsToCheck.forEach((text) => {
          extractVariableNames(text).forEach((name) => varNames.add(name));
        });

        // Check if dependent variables have values
        if (varNames.size > 0) {
          const missingVars: string[] = [];

          varNames.forEach((name) => {
            const depVar = definedVariables?.find((v) => v.name === name);
            if (
              !depVar ||
              depVar.value === null ||
              depVar.value === undefined ||
              depVar.value === ""
            ) {
              missingVars.push(name);
            }
          });

          // Show toast only if dependent variables don't have values
          if (missingVars.length > 0) {
            const depVarsList = missingVars
              .map((name) => `{{${name}}}`)
              .join(", ");
            toast({
              title: "Dependent Variables Missing .",
              description: `Variable {{${variable.name}}} depends on Variable ${depVarsList}. Please refresh it first.`,
              variant: "destructive",
              duration: 4000,
            });
            return;
          }
          // If all dependent variables have values, proceed with refresh below
        }
      }
    }

    // No dependencies, proceed with normal refresh
    tempVar.current = variable;

    const depBundleId = variable?.sourceBundleId;
    const depRequestId = variable?.sourceRequestId;

    // Use current state from logicType instead of saved data
    const depAllBundles =
      logicType?.find((logic) => logic?.name === selectedLogicName)?.data || [];

    // Find the bundle first with more flexible matching
    const depBundle = depAllBundles?.find((bundle: any) => {
      return (
        bundle?.bundleId == depBundleId ||
        bundle?.id == depBundleId ||
        String(bundle?.bundleId) === String(depBundleId) ||
        String(bundle?.id) === String(depBundleId)
      );
    });

    if (!depBundle) {
      console.error(
        " No bundle found. Available bundle IDs:",
        depAllBundles?.map((b: any) => ({ bundleId: b?.bundleId, id: b?.id })),
      );
      toast({
        title: "Error",
        description: `Could not find the bundle for variable {{${variable.name}}}.`,
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    // Find the request data from the bundle's current state (finalRequestData has the updated values)
    const depRequestDataItem = depBundle?.finalRequestData?.request?.find(
      (req: any) => {
        return (
          req?.requestId == depRequestId ||
          req?.id == depRequestId ||
          String(req?.requestId) === String(depRequestId) ||
          String(req?.id) === String(depRequestId)
        );
      },
    );

    if (!depRequestDataItem) {
      console.error(
        "No request found. Available request IDs:",
        depBundle?.requests?.map((r: any) => ({
          requestId: r?.requestId,
          id: r?.id,
        })),
      );
      toast({
        title: "Error",
        description: `Could not find the source request for variable {{${variable.name}}}.`,
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    // Interpolate variables in the request data
    const interpolatedHeaders = (depRequestDataItem.headers || []).map(
      (h: any) => ({
        key: interpolateDefinedVariablesOnly(h.key, definedVariables),
        value: interpolateDefinedVariablesOnly(h.value, definedVariables),
      }),
    );

    // Determine body_type and raw_type first
    let bodyType =
      depRequestDataItem.body_type || depRequestDataItem.bodyType || "none";
    // Normalize bodyType to valid enum
    if (bodyType === "form-urlencoded") bodyType = "x-www-form-urlencoded";
    // const rawType =
    //   bodyType === "none"
    //     ? null
    //     : depRequestDataItem.raw_type || depRequestDataItem.rawBodyType || "text";

    // Handle body based on body_type
    let interpolatedBody: any = "";
    if (bodyType === "x-www-form-urlencoded") {
      // Handle form data - check if body is already an object or if formData array exists
      if (
        typeof depRequestDataItem.body === "object" &&
        !Array.isArray(depRequestDataItem.body) &&
        depRequestDataItem.body !== null
      ) {
        // Body is already an object, interpolate its values
        const formDataObj: any = {};
        Object.entries(depRequestDataItem.body).forEach(([key, value]) => {
          formDataObj[key] = interpolateDefinedVariablesOnly(
            String(value || ""),
            definedVariables,
          );
        });
        interpolatedBody = formDataObj;
      } else if (
        depRequestDataItem.formData &&
        Array.isArray(depRequestDataItem.formData)
      ) {
        // Use formData array
        const formDataObj: any = {};
        depRequestDataItem.formData.forEach((item: any) => {
          if (item.enabled !== false) {
            formDataObj[item.key] = interpolateDefinedVariablesOnly(
              item.value || "",
              definedVariables,
            );
          }
        });
        interpolatedBody = formDataObj;
      } else {
        // Fallback to empty object
        interpolatedBody = {};
      }
    } else {
      // Handle other body types (raw, json, etc.)
      interpolatedBody = interpolateDefinedVariablesOnly(
        depRequestDataItem.body || "",
        definedVariables,
      );
    }

    const interpolatedToken = interpolateDefinedVariablesOnly(
      depRequestDataItem.auth?.token || "",
      definedVariables,
    );

    const interpolatedUrl = interpolateDefinedVariablesOnly(
      depRequestDataItem.url || "",
      definedVariables,
    );

    // Parse URL to extract query parameters
    const urlQueryParams: Array<{ key: string; value: string }> = [];
    try {
      const urlObj = new URL(interpolatedUrl);
      urlObj.searchParams.forEach((value, key) => {
        urlQueryParams.push({ key, value });
      });
    } catch (e) {
      // If URL parsing fails, continue without URL params
    }

    // Interpolate query params if they exist
    const interpolatedQueryParams = (depRequestDataItem.queryParams || []).map(
      (param: any) => ({
        enabled: param.enabled ?? true,
        key: interpolateDefinedVariablesOnly(param.key || "", definedVariables),
        value: interpolateDefinedVariablesOnly(
          param.value || "",
          definedVariables,
        ),
      }),
    );

    // Merge URL query params with queryParams array (URL params take precedence)
    const allQueryParams = [...interpolatedQueryParams];
    urlQueryParams.forEach((urlParam) => {
      const existingIndex = allQueryParams.findIndex(
        (p) => p.key === urlParam.key,
      );
      if (existingIndex >= 0) {
        // Replace existing param with URL param
        allQueryParams[existingIndex] = { ...urlParam, enabled: true };
      } else {
        // Add new param from URL
        allQueryParams.push({ ...urlParam, enabled: true });
      }
    });

    // Build params object from merged query params
    const params =
      allQueryParams.length > 0
        ? {
            type: "params",
            parameter: allQueryParams.map((p: any) => ({
              key: p.key,
              value: p.value,
            })),
          }
        : {
            type: "none",
            parameter: [],
          };

    // Map auth type to backend-expected format
    const authTypeMapping: Record<string, string> = {
      basic: "basic_auth",
      apiKey: "api_key",
      bearer: "bearer_token",
      jwt: "jwt_token",
      none: "none",
    };

    const rawAuthType = depRequestDataItem.auth?.type || "";
    const mappedAuthType = authTypeMapping[rawAuthType] || rawAuthType;

    // Check if config variables are present in the request
    const hasConfigVariables =
      /\{\{\s*config\.\w+\s*\}\}/.test(interpolatedUrl) ||
      /\{\{\s*config\.\w+\s*\}\}/.test(interpolatedBody) ||
      /\{\{\s*config\.\w+\s*\}\}/.test(interpolatedToken) ||
      interpolatedHeaders.some(
        (h: any) =>
          /\{\{\s*config\.\w+\s*\}\}/.test(h.key) ||
          /\{\{\s*config\.\w+\s*\}\}/.test(h.value),
      ) ||
      interpolatedQueryParams.some(
        (p: any) =>
          /\{\{\s*config\.\w+\s*\}\}/.test(p.key) ||
          /\{\{\s*config\.\w+\s*\}\}/.test(p.value),
      );

    // If config variables exist but no configuration is selected, show error and return
    if (
      hasConfigVariables &&
      !selectedConfigData &&
      selectedConfigIndex === null
    ) {
      toast({
        title: "Configuration Required",
        description:
          "This request requires configuration values. Please select a configuration.",
        variant: "destructive",
        duration: 4000,
      });
      return;
    }

    // Use selected configuration data based on selectedConfigIndex
    let configData = {};
    let configInfo = config;

    // Use selectedConfigData (always use first item if it's an array)
    if (selectedConfigData) {
      const configToUse = Array.isArray(selectedConfigData)
        ? selectedConfigData[0]
        : selectedConfigData;

      if (configToUse) {
        configInfo = {
          tabId: selectedPage?.uid || config.tabId,
          uid: configToUse.uid,
          configType: "temp",
        };

        configData = Object.fromEntries(
          Object.entries(configToUse).filter(
            ([key]) => !EXCLUDE_DATA_KEYS.includes(key),
          ),
        );
      }
    } else if (selectedConfigIndex !== null && forms[selectedConfigIndex]) {
      // Fallback to selectedConfigIndex if selectedConfigData is not available
      const selectedConfig = forms[selectedConfigIndex];
      configInfo = {
        tabId: selectedPage?.uid || config.tabId,
        uid: selectedConfig.uid,
        configType: "temp",
      };

      configData = Object.fromEntries(
        Object.entries(selectedConfig).filter(
          ([key]) => !EXCLUDE_DATA_KEYS.includes(key),
        ),
      );
    }

    const payload = {
      requestId: depRequestDataItem.requestId,
      plugin_id: pluginId || "",
      url: interpolatedUrl,
      method: depRequestDataItem.method || "GET",
      configuration_Id: configInfo?.uid || Date.now().toString(),
      params: params,
      headers: interpolatedHeaders,
      ssl_verify: depRequestDataItem.ssl_verify ?? true,
      auth: {
        type: mappedAuthType,
        ...(depRequestDataItem.auth?.token
          ? {
              token: interpolatedToken,
            }
          : {}),
        ...(depRequestDataItem.auth?.username
          ? {
              username: interpolateDefinedVariablesOnly(
                depRequestDataItem.auth?.username || "",
                definedVariables,
              ),
            }
          : {}),
        ...(depRequestDataItem.auth?.password
          ? {
              password: interpolateDefinedVariablesOnly(
                depRequestDataItem.auth?.password || "",
                definedVariables,
              ),
            }
          : {}),
      },
      body: interpolatedBody !== "" ? interpolatedBody : null,
      body_type: bodyType,
      raw_type:
        (depRequestDataItem.body_type || depRequestDataItem.bodyType) === "raw"
          ? depRequestDataItem.raw_type !== undefined
            ? depRequestDataItem.raw_type
            : depRequestDataItem.rawBodyType !== undefined
              ? depRequestDataItem.rawBodyType
              : null
          : null,
      isLoop: depRequestDataItem.isLoop || false,
      isSave: depRequestDataItem.isSave || false,
      loopVariable: depRequestDataItem.loopVariable || null,
      loopSourcePath: depRequestDataItem.loopSourcePath || null,
      loopSourceRequest: depRequestDataItem.loopSourceRequest || null,
      loopSourceRequestBundle:
        depRequestDataItem.loopSourceRequestBundle || null,
      checkpoint: depRequestDataItem.checkpoint || {
        produces: { enabled: false, sourcePath: null },
        consumes: { enabled: false, paramTemplate: null },
      },
      chainedFrom: depRequestDataItem.chainedFrom || null,
      flag: 1,
      config: configInfo,
      data: configData,
      pagination: depRequestDataItem.pagination
        ? depRequestDataItem.pagination.parameter !== undefined
          ? { ...depRequestDataItem.pagination, parameter: undefined }
          : depRequestDataItem.pagination
        : { type: "none" },
      filteration: depRequestDataItem.filteration
        ? depRequestDataItem.filteration.parameter !== undefined
          ? {
              type: depRequestDataItem.filteration.type || "none",
              default_cron_start_time:
                depRequestDataItem.filteration.default_cron_start_time ||
                "none",
            }
          : depRequestDataItem.filteration
        : {
            type: "none",
            default_cron_start_time: "none",
          },
      ...(depRequestDataItem.mappingId
        ? { mappingId: depRequestDataItem.mappingId }
        : {}),
      ...(depRequestDataItem.datalistId
        ? { datalistId: depRequestDataItem.datalistId }
        : {}),
      ...(depRequestDataItem.type_threatlookup
        ? { type_threatlookup: depRequestDataItem.type_threatlookup }
        : {}),
    };

    setAuthVariable.mutate(payload);
  };

  // function to render each variable
  const renderVariable = (variable: DefinedVariable, index: number) => (
    <div
      key={variable.name}
      className={`border 
        p-2 
        rounded-md 
        bg-background 
        flex items-center 
        justify-between 
        hover:bg-muted-background 
        hover:shadow-md
        transition-shadow
        `}
      onClick={() =>
        variable.value &&
        variable.value !== "—" &&
        handleVariableClick(variable)
      }
      onMouseEnter={() => setHoveredIndex(index)}
      onMouseLeave={() => setHoveredIndex(null)}
    >
      <div className="flex items-center gap-1">
        <Braces size={22} className="text-muted-foreground" />
        <div className="flex flex-col">
          <span className="text-xs text-muted-foreground font-semibold max-w-[90px] truncate">
            {`{{${variable.name}}}`}
          </span>
          <span
            className="text-muted-foreground truncate w-[90px]"
            style={{ fontSize: "9px" }}
          >
            {renderVariablePreview(variable)}
          </span>
        </div>
      </div>

      {hoveredIndex === index && (
        <div className="flex items-center gap-1 p-1">
          <RefreshCcw
            size={15}
            className=" 
              text-muted-foreground
              hover:rotate-[360deg]
              hover:transition-transform
              hover:duration-700
              transition-none
              hover:text-primaryGreen
              scale-x-[-1]
              "
            onClick={(e) => {
              e.stopPropagation();
              handleIsAuthRefresh(variable);
            }}
          />

          {/* 
          <Copy
            className="text-muted-foreground hover:text-primaryBlue"
            size={15}
            onClick={(e) => {
              e.stopPropagation();
              handleVariableClick(variable);
            }}
          /> */}

          <Trash2
            className="text-muted-foreground hover:text-primary"
            size={15}
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteVariable(variable.id);
            }}
          />
        </div>
      )}
    </div>
  );

  // Reset selected config index when settings page changes
  React.useEffect(() => {
    setSelectedConfigIndex(null);
  }, [selectedSettingsPageIndex, selectedPage?.uid]);

  const matchingConfig = configurationData?.find(
    (cfg) => cfg.pageUid == selectedPage?.uid,
  );

  const forms = matchingConfig?.data || [];

  // Sync selectedConfigIndex with selectedConfigData from props
  React.useEffect(() => {
    if (selectedConfigData && forms.length > 0) {
      const index = forms.findIndex((form: any) => {
        return (
          form.uid === selectedConfigData.uid ||
          JSON.stringify(form) === JSON.stringify(selectedConfigData)
        );
      });

      if (index !== -1 && index !== selectedConfigIndex) {
        setSelectedConfigIndex(index);
      }
    } else if (!selectedConfigData && selectedConfigIndex !== null) {
      // Reset local index if selectedConfigData is cleared
      setSelectedConfigIndex(null);
    }
  }, [selectedConfigData, forms]);

  // Auto-select if only one settings page and one configuration form exist
  React.useEffect(() => {
    if (
      option === "logic" &&
      selectedSettingsPageIndex === null &&
      uiPages?.filter((p) => p.pageType === "settings").length === 1
    ) {
      dispatch({
        type: MeshPluginActions.SET_SELECTED_SETTINGS_PAGE_INDEX,
        payload: 0,
      });
    }
  }, [option, uiPages, selectedSettingsPageIndex]);

  // Auto-select if only one configuration form exists for the selected settings page
  React.useEffect(() => {
    if (
      option === "logic" &&
      selectedSettingsPageIndex !== null &&
      selectedConfigIndex === null
    ) {
      const selectedPage =
        selectedSettingsPageIndex !== undefined &&
        selectedSettingsPageIndex !== null
          ? uiPages?.[selectedSettingsPageIndex]
          : undefined;
      const matchingConfig = configurationData?.find(
        (cfg) => cfg.pageUid === selectedPage?.uid,
      );
      const formList = matchingConfig?.data || [];

      if (formList.length === 1) {
        setSelectedConfigIndex?.(0);
        dispatch({
          type: MeshPluginActions.SET_CONFIG_VARIABLES,
          payload: [formList[0]],
        });
        dispatch({
          type: MeshPluginActions.SET_SELECTED_CONFIG_DATA,
          payload: formList[0],
        });
      }
    }
  }, [
    option,
    selectedSettingsPageIndex,
    selectedConfigIndex,
    uiPages,
    configurationData,
    setSelectedConfigIndex,
  ]);

  // copy configuration variable on click
  const handleCopyConfigs = (e: any, key: any) => {
    e.stopPropagation();
    const configVariable = `{{config.${key}}}`;
    navigator.clipboard.writeText(configVariable).catch(() => {});
    toast({
      title: "Copied!",
      description: `Configuration ${configVariable} copied.`,
      variant: "info",
      duration: 2000,
    });
  };

  // Confirmation dialog handlers
  const onDiscardAndContinue = () => {
    setUnsavedConfirmationOpen(false);
    dispatch({
      type: MeshPluginActions.SET_HAS_UNSAVED_HELP_DOCS,
      payload: false,
    });
    dispatch({
      type: MeshPluginActions.SET_HAS_UNSAVED_FIELDS,
      payload: false,
    });
    dispatch({
      type: MeshPluginActions.SET_HAS_UNSAVED_GENERAL_INFO,
      payload: false,
    });

    // Delay navigation to next tick to ensure state updates
    setTimeout(() => {
      if (pendingPageKey !== null) {
        goToPage?.(pendingPageKey);
        setPendingPageKey(null);
      }
    }, 0);
  };

  // on close confirmation handler
  const onCloseUnsavedConfirmation = () => {
    setUnsavedConfirmationOpen(false);
    setPendingPageKey(null);
  };

  // function to delete the variable and show confirmation
  const handleDeleteVariable = (variableId: string | number | undefined) => {
    setVariableToDelete(variableId);
    setConfirmationOpen(true);
  };

  // to delete variable after confirmation
  const onDelete = () => {
    if (variableToDelete == null || !definedVariables || !setDefinedVariables)
      return;
    // check if variable is saved in server
    const isSavedVariable = logicPageInfo?.data?.variables?.some(
      (variable: any) => variable.id == variableToDelete,
    );

    // delete from state if unsaved variable
    if (!isSavedVariable) {
      const variableName = definedVariables.find(
        (variable) => variable.id === variableToDelete,
      )?.name;

      const filteredVariables = definedVariables.filter(
        (variable) => variable.id !== variableToDelete,
      );
      setDefinedVariables(filteredVariables);
      toast({
        title: "Deleted!",
        description: `Variable "${variableName}" has been deleted.`,
        variant: "success",
        duration: 2000,
      });
      setConfirmationOpen(false);
      setVariableToDelete(null);
    } else {
      // call the delete api mutation if the data is already saved
      const payload = {
        plugin_id: pluginId || "",
        delete_type: "variable",
        bundle_uid: "",
        request_uid: "",
        variable_uid: variableToDelete.toString(),
      };
      deleteVariable.mutate(payload);
    }
  };

  // handle select config
  const handleSelectConfig = (index: any, formData: any) => {
    setSelectedConfigIndex(index);
    dispatch({
      type: MeshPluginActions.SET_CONFIG_VARIABLES,
      payload: [formData],
    });
    dispatch({
      type: MeshPluginActions.SET_SELECTED_CONFIG_DATA,
      payload: formData,
    });
    toast({
      title: `" ${formData?.title} " configuration selected`,
      description: `You can now use this configuration's fields in your variables and requests.`,
      variant: "success",
      duration: 3000,
    });
  };

  const settings =
    uiPages && uiPages.filter((item) => item.pageType == "settings");

  const currentLogicType = logicType?.find(
    (item) => item.name === selectedLogicName,
  );

  return (
    <div className="flex items-start h-[100%] gap-5">
      <PageLoader show={loading} />
      {confirmationOpen && (
        <Confirmation
          text={"Are you sure , you want to delete this variable ?"}
          confirmAction={onDelete}
          rejectAction={onClose}
          confirmText="Yes, delete it!"
          rejectText="Cancel"
          show={confirmationOpen}
          closeDialog={onClose}
        />
      )}

      <Confirmation
        text={
          hasUnsavedHelpDocs && hasUnsavedFields && hasUnsavedGeneralInfo
            ? "You have unsaved Help Documents, Action Fields, and General Information. Do you want to discard and continue?"
            : hasUnsavedHelpDocs && hasUnsavedFields
              ? "You have unsaved Help Documents and Action Fields. Do you want to discard and continue?"
              : hasUnsavedHelpDocs && hasUnsavedGeneralInfo
                ? "You have unsaved Help Documents and General Information. Do you want to discard and continue?"
                : hasUnsavedFields && hasUnsavedGeneralInfo
                  ? "You have unsaved Action Fields and General Information. Do you want to discard and continue?"
                  : hasUnsavedHelpDocs
                    ? "Help Documents not saved. Do you want to discard and continue?"
                    : hasUnsavedFields
                      ? "Action Fields not saved. Do you want to discard and continue?"
                      : "General Information not saved. Do you want to continue?"
        }
        confirmAction={onDiscardAndContinue}
        rejectAction={onCloseUnsavedConfirmation}
        confirmText="Discard and Continue"
        rejectText="Keep Editing"
        show={unsavedConfirmationOpen}
        closeDialog={onCloseUnsavedConfirmation}
      />

      <div className="w-[300px] max-h-[100%] rounded-lg mb-2 flex flex-col gap-2 cursor-pointer mt-1">
        <div className="flex items-center border-b mb-1 pb-1">
          <Plug2 size={30} className="text-primary rotate-90 ml-1 mr-1" />
          <p className="text-[23px] font-semibold pl-1 flex-shrink-0">
            MeshConnectX
          </p>
        </div>

        {/*  sidebar */}

        {options.map((item) => {
          const isSelected = item === selectedOption;
          return (
            <div
              key={item}
              onClick={() => handleChangePage(item)}
              className={`w-full rounded-lg border-2 text-sm bg-primary-background ${
                isSelected ? "border-primary" : "border-secondary-background"
              } ${item === "Logic" && isSelected ? "flex flex-col flex-1 overflow-hidden p-3" : "p-3 flex-shrink-0"}`}
            >
              {item === "Logic" ? (
                <>
                  <div className="flex-shrink-0">{item}</div>
                  <div className="flex-1 overflow-y-auto min-h-0 mt-2 pr-1">
                    {settings &&
                      settings.length > 0 &&
                      configurationData &&
                      configurationData.length > 0 && (
                        <div className="text-sm text-muted-foreground border-y rounded-md cursor-default">
                          <div
                            className="flex items-center justify-between cursor-pointer bg-muted p-2 rounded-md"
                            onClick={() =>
                              setIsConfigDataCollapsed(!isConfigDataCollapsed)
                            }
                          >
                            <div className="flex items-center gap-1">
                              <p className="text-xs text-foreground font-semibold px-1">
                                Configuration data
                              </p>
                            </div>
                            <div className="flex items-center gap-1 mr-1">
                              <InfoTooltip text="Choose a configuration, and use its field in your request(s) with this format : {{config.<field_name>}}" />
                              {isConfigDataCollapsed ? (
                                <div title="expand">
                                  <ChevronDown
                                    size={18}
                                    className="text-muted-foreground"
                                  />
                                </div>
                              ) : (
                                <div title="collapse">
                                  <ChevronUp
                                    size={18}
                                    className="text-muted-foreground"
                                  />
                                </div>
                              )}
                            </div>
                          </div>

                          <div
                            className={`overflow-hidden transition-all duration-500 ease-in-out ${
                              isConfigDataCollapsed
                                ? "max-h-0"
                                : "max-h-[1000px]"
                            }`}
                          >
                            {/* Settings Tabs */}
                            {Array.isArray(uiPages) &&
                              uiPages.filter((p) => p.pageType === "settings")
                                .length > 0 && (
                                <div className="mt-1 bg-white p-2 border rounded-lg">
                                  {/* <p className="text-sm font-semibold text-muted-foreground mb-2">Settings Pages:</p> */}
                                  {/* Tabs (horizontal) */}
                                  <label className="text-xs text-foreground">
                                    {" "}
                                    Choose Settings{" "}
                                  </label>

                                  <div className="flex gap-2 mt-1 mb-1 flex-wrap">
                                    {uiPages
                                      .filter(
                                        (page) =>
                                          page.pageType === "settings" &&
                                          page.pageTitle &&
                                          page.pageTitle.trim() !== "",
                                      )
                                      .map((page, index) => {
                                        // Find the index in the original uiPages array for correct selection
                                        const originalIndex = uiPages.findIndex(
                                          (p) => p.uid === page.uid,
                                        );
                                        const isActive =
                                          selectedSettingsPageIndex ===
                                          originalIndex;

                                        return (
                                          <button
                                            key={page.uid || index}
                                            onClick={() => {
                                              dispatch({
                                                type: MeshPluginActions.SET_SELECTED_SETTINGS_PAGE_INDEX,
                                                payload: originalIndex,
                                              });
                                              dispatch({
                                                type: MeshPluginActions.SET_SELECTED_FORM_INDEX,
                                                payload: null,
                                              });
                                            }}
                                            className={`px-4 py-2 rounded-lg border w-full mb-1 text-sm ${
                                              isActive
                                                ? "bg-primary text-background"
                                                : "bg-muted text-muted-foreground"
                                            }`}
                                          >
                                            {page.pageTitle}
                                          </button>
                                        );
                                      })}
                                  </div>

                                  {/* Show Forms */}
                                  <div className="w-full bg-white rounded-lg mt-1">
                                    <label className="text-xs text-foreground">
                                      {" "}
                                      Choose Configuration{" "}
                                    </label>

                                    {selectedSettingsPageIndex !== null &&
                                    configurationData &&
                                    forms.length > 0 ? (
                                      <div className="mb-2 mt-1">
                                        {forms.map(
                                          (formData: any, index: number) => {
                                            const isSelected =
                                              selectedConfigIndex === index;

                                            return (
                                              <button
                                                key={formData.uid || index}
                                                className={`px-4 py-2 mr-2 rounded-lg text-sm border w-full mb-1 ${
                                                  isSelected
                                                    ? "bg-primary text-background "
                                                    : "bg-muted text-muted-foreground border-secondary"
                                                }`}
                                                onClick={() =>
                                                  handleSelectConfig(
                                                    index,
                                                    formData,
                                                  )
                                                }
                                              >
                                                {formData.title ||
                                                  `Form ${index + 1}`}
                                              </button>
                                            );
                                          },
                                        )}
                                      </div>
                                    ) : (
                                      <div>
                                        <p className="text-xs text-muted-foreground italic px-1 mt-2 mb-1">
                                          No configuration found. Create a
                                          configuration form in the settings
                                          page to see it here.
                                        </p>
                                      </div>
                                    )}
                                  </div>

                                  {selectedConfigIndex !== null &&
                                    forms[selectedConfigIndex] && (
                                      <div className="rounded-md mt-1">
                                        <p className="text-xs px-1 capitalize text-foreground mb-2">
                                          Configuration varaibles
                                        </p>
                                        {/* show the selected settings and the configuration */}
                                        {/* <p className="text-primary mb-1 text-md px-1">
                                        {(uiPages &&
                                          selectedSettingsPageIndex !== null &&
                                          selectedSettingsPageIndex !==
                                            undefined &&
                                          uiPages[selectedSettingsPageIndex]
                                            ?.pageTitle) ||
                                          "--"}
                                        {" > "}{" "}
                                        {(selectedConfigIndex !== null &&
                                          forms[selectedConfigIndex]?.title) ||
                                          "--"}
                                      </p> */}
                                        <div className="flex flex-col gap-1 text-sm text-muted-foreground pb-1">
                                          {Object.entries(
                                            forms[selectedConfigIndex],
                                          )
                                            .filter(
                                              ([key]) =>
                                                ![
                                                  "uid",
                                                  "pluginId",
                                                  "userID",
                                                  "epoch",
                                                  "pageUid",
                                                  "title",
                                                  "action",
                                                ].includes(key),
                                            )
                                            .map(([key]) => (
                                              <div
                                                key={key}
                                                className="flex items-center cursor-pointer justify-between border p-2 rounded-md bg-background relative 
                                          group hover:shadow-md
                                           transition-shadow "
                                                onClick={(e) =>
                                                  handleCopyConfigs(e, key)
                                                }
                                              >
                                                <span className="text-xs w-[90%] truncate font-semibold">
                                                  {key}
                                                </span>
                                                <Copy
                                                  className="text-muted-foreground hover:text-primaryBlue opacity-0 group-hover:opacity-100 transition cursor-pointer"
                                                  size={13}
                                                />
                                              </div>
                                            ))}
                                        </div>
                                      </div>
                                    )}
                                </div>
                              )}
                          </div>
                        </div>
                      )}
                    {commonVariables && commonVariables.length > 0 && (
                      <div className="flex flex-col px-1 mt-1 mb-2 bg-muted">
                        <div
                          className="flex items-center justify-between text-muted-foreground cursor-pointer  p-2 rounded-md hover:bg-muted/80 transition-colors duration-200"
                          onClick={() =>
                            setIsCommonVariablesCollapsed(
                              !isCommonVariablesCollapsed,
                            )
                          }
                        >
                          <p className="text-foreground text-xs font-semibold">
                            Common Variable
                            {commonVariables.length > 1 ? "s" : ""}
                          </p>
                          <div className="flex items-center gap-1">
                            <InfoTooltip
                              text={`Common variables are available across all the bundles created in this ${currentLogicType?.name} logic type.`}
                            />
                            {isCommonVariablesCollapsed ? (
                              <div title="expand">
                                <ChevronDown
                                  size={18}
                                  className="text-muted-foreground"
                                />
                              </div>
                            ) : (
                              <div title="collapse">
                                <ChevronUp
                                  size={18}
                                  className="text-muted-foreground"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                        <div
                          className={`overflow-hidden transition-all duration-300 ease-in-out ${
                            isCommonVariablesCollapsed
                              ? "max-h-0"
                              : "max-h-[1000px]"
                          }`}
                        >
                          <div className="flex flex-col gap-2 mb-1 px-1 pb-1 mt-2">
                            {commonVariables.map((variable, index) =>
                              renderVariable(variable, index),
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {nonCommonVariables && nonCommonVariables.length > 0 && (
                      <div className="flex flex-col px-1 mb-2 bg-muted">
                        <div
                          className="flex items-center justify-between text-muted-foreground cursor-pointer  p-2 rounded-md hover:bg-muted/80 transition-colors duration-200"
                          onClick={() =>
                            setIsLocalVariablesCollapsed(
                              !isLocalVariablesCollapsed,
                            )
                          }
                        >
                          <p className="text-xs text-foreground font-semibold flex flex-col">
                            Local Variable
                            {nonCommonVariables.length > 1 ? "s " : " "}
                            <span className="font-normal text-muted-foreground">
                              Bundle{" - "}
                              {bundleTitle}
                            </span>
                          </p>
                          {isLocalVariablesCollapsed ? (
                            <div title="expand">
                              <ChevronDown
                                size={18}
                                className="text-muted-foreground"
                              />
                            </div>
                          ) : (
                            <div title="collapse">
                              <ChevronUp
                                size={18}
                                className="text-muted-foreground"
                              />
                            </div>
                          )}
                        </div>
                        <div
                          className={`overflow-hidden transition-all duration-300 ease-in-out ${
                            isLocalVariablesCollapsed
                              ? "max-h-0"
                              : "max-h-[1000px]"
                          }`}
                        >
                          <div className="flex flex-col gap-2 mb-1 px-1 pb-1 mt-2">
                            {nonCommonVariables.map((variable, index) =>
                              renderVariable(
                                variable,
                                index + (commonVariables?.length || 0),
                              ),
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                item
              )}
            </div>
          );
        })}
      </div>

      {/*  main content */}

      <div className="p-5 bg-primary-background w-full h-[95%] rounded-md ">
        <h3 className="m-0 mb-6 text-center">{title}</h3>
        <div className="flex pr-1 pl-1 flex-col gap-3 h-[90%] overflow-y-auto ">
          {children}
        </div>
      </div>
    </div>
  );
}
